import { useState } from 'react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DrawerWithCloseButton } from '@/components/ui/drawer'
import { Separator } from '@/components/ui/separator'
import { SkeletonRows } from '@/components/ui/skeleton'
import { documentTypeLabel, type NamedDocument, useDocumentParseDetail } from '@/models/document'
import { DatesEditor } from './DatesEditor'
import { formatDate } from './format'

const DATES: { title: string; fields: [string, string][] } = {
  title: 'Dates',
  fields: [
    ['dateEffective', 'Effective'],
    ['dateExpiration', 'Expires'],
    ['dateIssued', 'Issued'],
  ],
}

/** Grouped the way the agent's schema is, so a reader can find a field. */
const FIELD_GROUPS: { title: string; fields: [string, string][] }[] = [
  DATES,
  {
    title: 'Identifiers',
    fields: [
      ['policyNumber', 'Policy number'],
      ['quoteNumber', 'Quote number'],
      ['cdlNumber', 'CDL number'],
      ['cdlState', 'CDL state'],
      ['cdlClass', 'CDL class'],
      ['cdlEndorsements', 'CDL endorsements'],
      ['dotNumber', 'DOT number'],
      ['mcNumber', 'MC number'],
      ['claimNumbers', 'Claim numbers'],
      ['vins', 'VINs'],
    ],
  },
  {
    title: 'Parties',
    fields: [
      ['insurerName', 'Insurer'],
      ['namedInsured', 'Named insured'],
      ['personName', 'Person'],
      ['agencyName', 'Agency'],
    ],
  },
]

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

function formatValue(value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null
  if (Array.isArray(value)) return value.length ? value.map(String).join(', ') : null
  // The parser stores dates as ISO; people read them as "Sep 14, 2026".
  if (typeof value === 'string' && ISO_DATE.test(value)) return formatDate(value)
  return String(value)
}

function FieldRow({ label, value }: { label: string; value: unknown }) {
  const text = formatValue(value)
  if (!text) return null
  return (
    <div className="grid grid-cols-[9rem_1fr] gap-3 py-1 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium break-words">{text}</span>
    </div>
  )
}

function Group({
  title,
  fields,
  data,
}: {
  title: string
  fields: [string, string][]
  data: Record<string, unknown>
}) {
  const rendered = fields.filter(([key]) => formatValue(data[key]) !== null)
  if (!rendered.length) return null
  return (
    <div className="flex flex-col gap-1">
      <h3 className="text-muted-foreground text-xs font-semibold uppercase">{title}</h3>
      {rendered.map(([key, label]) => (
        <FieldRow key={key} label={label} value={data[key]} />
      ))}
    </div>
  )
}

/**
 * Mounted for the life of the page so the drawer can animate both ways; vaul
 * unmounts the body itself, which is what stops the parse query.
 */
export function DocumentMetadataDrawer({
  orgId,
  document,
  attachmentId,
  open,
  onOpenChange,
}: {
  orgId: string | null | undefined
  document: NamedDocument | null
  /** Focus on one file's extraction; omit for the document's roll-up. */
  attachmentId?: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  return (
    <DrawerWithCloseButton
      open={open}
      onOpenChange={onOpenChange}
      handleClose={() => onOpenChange(false)}
      title={document?.displayName ?? 'Document metadata'}
      className="w-full! sm:max-w-lg!"
    >
      {document && <MetadataBody orgId={orgId} document={document} attachmentId={attachmentId} />}
    </DrawerWithCloseButton>
  )
}

function MetadataBody({
  orgId,
  document,
  attachmentId,
}: {
  orgId: string | null | undefined
  document: NamedDocument
  /** One file's own extraction rather than the document's roll-up. */
  attachmentId?: string | null
}) {
  const { detail, isLoading } = useDocumentParseDetail(orgId, document.id)
  const [editingDates, setEditingDates] = useState(false)
  const attachment = attachmentId ? detail?.attachments.find((a) => a.id === attachmentId) : undefined
  const data = attachment?.data ?? detail?.data ?? {}
  const status = attachment ? attachment.status : detail?.status
  const error = attachment ? attachment.error : detail?.error
  const otherDates = Array.isArray(data.otherDates)
    ? (data.otherDates as { label: string; value: string }[])
    : []
  const warnings = Array.isArray(data.warnings) ? (data.warnings as string[]) : []

  return (
    <>
      <div className="flex flex-col gap-1 pb-5">
        <h2 className="text-lg font-semibold">{attachment?.fileName ?? document.displayName}</h2>
        <p className="text-muted-foreground text-sm">
          {attachment
            ? 'Everything the parser read off this file.'
            : 'Everything the parser read off this document.'}
        </p>
      </div>

      {isLoading || !detail ? (
        <SkeletonRows rows={6} />
      ) : (
        <div className="flex flex-col gap-5">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline">{documentTypeLabel(detail.documentType)}</Badge>
            {detail.suggestedDocumentType && (
              <Badge variant="outline" className="text-(--amber-text)">
                Looks like {documentTypeLabel(detail.suggestedDocumentType)}
              </Badge>
            )}
            {status && <Badge variant="secondary">{status}</Badge>}
          </div>

          {error && (
            <p className="text-sm text-(--red-text)">
              {attachment ? 'Parse failed' : `Parse failed after ${detail.attempts} attempt(s)`}: {error}
            </p>
          )}

          {formatValue(data.summary) && <p className="text-sm">{String(data.summary)}</p>}

          {editingDates && orgId ? (
            <DatesEditor
              orgId={orgId}
              documentId={document.id}
              attachmentId={attachment?.id}
              data={data}
              onDone={() => setEditingDates(false)}
            />
          ) : (
            <>
              <Group title={DATES.title} fields={DATES.fields} data={data} />
              {/* One file, or one picked: otherwise the dates have no single file to land on. */}
              {orgId && (attachment || detail.attachments.length <= 1) && (
                <Button variant="outline" size="xs" className="w-fit" onClick={() => setEditingDates(true)}>
                  Edit dates
                </Button>
              )}
            </>
          )}
          {FIELD_GROUPS.filter((group) => group !== DATES).map((group) => (
            <Group key={group.title} title={group.title} fields={group.fields} data={data} />
          ))}

          {otherDates.length > 0 && (
            <div className="flex flex-col gap-1">
              <h3 className="text-muted-foreground text-xs font-semibold uppercase">Other dates</h3>
              {otherDates.map((entry) => (
                <FieldRow key={entry.label} label={entry.label} value={entry.value} />
              ))}
            </div>
          )}

          {warnings.length > 0 && (
            <div className="flex flex-col gap-1">
              <h3 className="text-muted-foreground text-xs font-semibold uppercase">Warnings</h3>
              {warnings.map((warning) => (
                <p key={warning} className="text-sm text-(--amber-text)">
                  {warning}
                </p>
              ))}
            </div>
          )}

          {/* Per file, because a policy uploaded as two files has two extractions. */}
          {!attachment && detail.attachments.length > 0 && (
            <>
              <Separator />
              <div className="flex flex-col gap-3">
                <h3 className="text-muted-foreground text-xs font-semibold uppercase">Files</h3>
                {detail.attachments.map((attachment) => (
                  <div key={attachment.id} className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                      {attachment.file ? (
                        <a
                          href={attachment.file}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm font-medium text-blue-600 hover:underline"
                        >
                          {attachment.fileName}
                        </a>
                      ) : (
                        <span className="text-sm font-medium">{attachment.fileName}</span>
                      )}
                      {attachment.status && (
                        <Badge variant="secondary" className="text-xs">
                          {attachment.status}
                        </Badge>
                      )}
                    </div>
                    {attachment.error && <p className="text-xs text-(--red-text)">{attachment.error}</p>}
                  </div>
                ))}
              </div>
            </>
          )}

          {detail.dateParsed && (
            <p className="text-muted-foreground text-xs">
              Parsed {new Date(detail.dateParsed).toLocaleString('en-US')}
            </p>
          )}
        </div>
      )}
    </>
  )
}
